/**
 * Life cycle of a spacecraft at a date (Sprint 3, D11). Pure; times are UTC ms.
 *
 * - `before-launch`: before its trajectory starts (or before launch without one): not shown.
 * - `active`: in flight and in contact: sprite and trail.
 * - `silent`: the mission has ended (contact lost) but the trajectory continues (Pioneer 10 after 2003-01-23):
 *   hollow sprite, label suffix "silent", dimmed trail.
 * - `ended`: past the end of the trajectory (Cassini after its plunge): sprite removed, an end marker stays.
 *
 * Landers that lift off again and rejoin their parent (S4-11, D10 — Apollo's Eagle):
 *
 * - `landed`: from touchdown to lift-off: on the surface, sprite at the landing site.
 * - `ascending`: from lift-off to the rejoin: the ascent stage flies; the descent stage stays as the site marker.
 * - `docked`: from the rejoin on: the ascent stage is part of its parent again and not drawn; the site marker stays.
 *
 * A lander without a lift-off keeps Sprint 3's behaviour: its trajectory ends at the landing (`ended`, Huygens).
 *
 * Independently of the state, everything after "now" (the real date) is a prediction: the trail is dashed there
 * (Concept §5).
 */

export type LifeState = 'before-launch' | 'active' | 'silent' | 'ended' | 'landed' | 'ascending' | 'docked';

export interface LifeCycle {
  /** Launch (or separation from a parent), UTC ms. */
  launch: number;
  /** End of the mission (loss of contact), UTC ms, or null while active. A plain date counts until its end. */
  end: number | null;
  /** Trajectory coverage [start, end], UTC ms, or null without a trajectory. */
  coverage: readonly [number, number] | null;
  /** Landers that lift off again (S4-11): touchdown, lift-off and rejoin with the parent, UTC ms. */
  landing?: number;
  liftoff?: number;
  rejoin?: number;
}

const DAY = 86_400_000;

/** Parses a mission `end` (YYYY-MM-DD or date-time): a plain date means the end of that day. */
export function parseEnd(end: string | null): number | null {
  if (end === null) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(end)) return Date.parse(`${end}T00:00:00Z`) + DAY;
  const ms = Date.parse(end);
  if (Number.isNaN(ms)) throw new Error(`Invalid end date: ${end}`);
  return ms;
}

export function lifeState(lc: LifeCycle, ms: number): LifeState {
  const start = lc.coverage ? lc.coverage[0] : lc.launch;
  if (ms < start) return 'before-launch';
  if (lc.rejoin !== undefined && ms >= lc.rejoin) return 'docked';
  if (lc.coverage && ms > lc.coverage[1]) return 'ended';
  if (lc.liftoff !== undefined && lc.landing !== undefined && ms >= lc.landing) {
    return ms < lc.liftoff ? 'landed' : 'ascending';
  }
  if (!lc.coverage && lc.end !== null && ms > lc.end) return 'ended';
  if (lc.end !== null && ms > lc.end) return 'silent';
  return 'active';
}

/** Whether the spacecraft itself is shown (sprite, label, Follow): in flight, silent or on the surface. */
export function isShown(state: LifeState | string | undefined): boolean {
  return state === 'active' || state === 'silent' || state === 'landed' || state === 'ascending';
}

/** Whether a site marker stays where the spacecraft landed (S4-11): from touchdown on, also after a rejoin. */
export function hasSiteMarker(state: LifeState): boolean {
  return state === 'landed' || state === 'ascending' || state === 'docked';
}

/** From when the spacecraft is silent (UTC ms), or null if it never is within its trajectory. */
export function silentFrom(lc: LifeCycle): number | null {
  if (lc.end === null || !lc.coverage || lc.end >= lc.coverage[1]) return null;
  return lc.end;
}

/** Whether a date lies in the future of the real "now" (predicted trajectory, drawn dashed). */
export function isPredicted(ms: number, nowMs: number): boolean {
  return ms > nowMs;
}
